import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Box,
  Stack,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  SlowMotionVideo as SlowIcon,
  Speed as FastIcon,
  PlayCircleOutline as NormalIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
} from '@mui/icons-material';
import { PlaybackSpeed, NarratorVoice, EVENT_TYPES } from '../../types/fashion-show';

export interface ReelSetupCardProps {
  onStart: (eventType: string, speed: PlaybackSpeed, voice: NarratorVoice) => void;
  onCancel: () => void;
}

/**
 * Pre-playback configuration UI for the Fashion Show Reel.
 *
 * Renders an event type selector dropdown listing all 18 event types,
 * a speed controller with three options (Slow/Normal/Fast) defaulting to Normal,
 * and a Fashion Show Button that is disabled until an event type is selected.
 *
 * Requirements: 1.2, 1.3, 1.4, 1.5, 4.1, 4.2, 4.3, 4.4
 */
const ReelSetupCard: React.FC<ReelSetupCardProps> = ({ onStart, onCancel }) => {
  const [selectedEventType, setSelectedEventType] = useState<string>('');
  const [speed, setSpeed] = useState<PlaybackSpeed>('normal');
  const [voice, setVoice] = useState<NarratorVoice>('male');

  const handleSpeedChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSpeed: PlaybackSpeed | null,
  ) => {
    if (newSpeed !== null) {
      setSpeed(newSpeed);
    }
  };

  const handleVoiceChange = (
    _event: React.MouseEvent<HTMLElement>,
    newVoice: NarratorVoice | null,
  ) => {
    if (newVoice !== null) {
      setVoice(newVoice);
    }
  };

  const handleStart = () => {
    if (selectedEventType) {
      onStart(selectedEventType, speed, voice);
    }
  };

  return (
    <Card
      sx={{
        maxWidth: 480,
        mx: 'auto',
        mt: 4,
        borderRadius: 3,
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
      }}
    >
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
          🎭 Fashion Show Reel
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          Select an event type and speed to start your fashion show
        </Typography>

        <Stack spacing={3}>
          {/* Event Type Selector */}
          <FormControl fullWidth>
            <InputLabel id="event-type-label">Event Type</InputLabel>
            <Select
              labelId="event-type-label"
              id="event-type-select"
              value={selectedEventType}
              label="Event Type"
              onChange={(e) => setSelectedEventType(e.target.value)}
            >
              {EVENT_TYPES.map((eventType) => (
                <MenuItem key={eventType} value={eventType}>
                  {eventType}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Speed Controller */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Playback Speed
            </Typography>
            <ToggleButtonGroup
              value={speed}
              exclusive
              onChange={handleSpeedChange}
              aria-label="playback speed"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="slow" aria-label="slow speed">
                <SlowIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Slow
              </ToggleButton>
              <ToggleButton value="normal" aria-label="normal speed">
                <NormalIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Normal
              </ToggleButton>
              <ToggleButton value="fast" aria-label="fast speed">
                <FastIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Fast
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Voice Selector */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Narrator Voice
            </Typography>
            <ToggleButtonGroup
              value={voice}
              exclusive
              onChange={handleVoiceChange}
              aria-label="narrator voice"
              fullWidth
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                },
              }}
            >
              <ToggleButton value="male" aria-label="male voice">
                <MaleIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Male
              </ToggleButton>
              <ToggleButton value="female" aria-label="female voice">
                <FemaleIcon sx={{ mr: 0.5, fontSize: 20 }} />
                Female
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              color="inherit"
              onClick={onCancel}
              startIcon={<CloseIcon />}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleStart}
              disabled={!selectedEventType}
              startIcon={<PlayArrowIcon />}
              fullWidth
              sx={{ fontWeight: 600 }}
            >
              Start Show
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ReelSetupCard;
